<template>
  <div class="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-secondary-900 px-4">
    <div class="max-w-md w-full text-center">
      <div class="text-9xl font-bold" :class="errorColorClass">{{ status }}</div>
      <h1 class="mt-4 text-3xl font-bold text-secondary-900 dark:text-white">{{ errorTitle }}</h1>
      <p class="mt-2 text-secondary-600 dark:text-secondary-400">
        {{ errorMessage }}
      </p>
      <p v-if="status === 403" class="mt-2 text-sm text-secondary-500 dark:text-secondary-500">
        If you believe this is an error, please contact your administrator.
      </p>
      <div class="mt-8 flex gap-3 justify-center">
        <button
          @click="goBack"
          class="inline-flex items-center justify-center px-4 py-2 bg-secondary-200 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-200 rounded-lg hover:bg-secondary-300 dark:hover:bg-secondary-600 transition-colors font-medium"
        >
          <ArrowLeftIcon class="h-5 w-5 mr-2" />
          Go Back
        </button>
        <Link
          href="/"
          class="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
        >
          <HomeIcon class="h-5 w-5 mr-2" />
          Go Home
        </Link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import { HomeIcon, ArrowLeftIcon } from '@heroicons/vue/24/outline';

// Use no layout for error pages (full-page design)
defineOptions({
  layout: null,
});

const props = defineProps({
  status: {
    type: Number,
    required: true,
  },
});

const errorTitle = computed(() => {
  const titles = {
    403: 'Access Denied',
    404: 'Page Not Found',
    419: 'Page Expired',
    429: 'Too Many Requests',
    500: 'Server Error',
    503: 'Service Unavailable',
  };
  return titles[props.status] || 'Error';
});

const errorMessage = computed(() => {
  const messages = {
    403: "You don't have permission to access this resource.",
    404: "Sorry, the page you are looking for could not be found.",
    419: 'Your session has expired. Please refresh and try again.',
    429: 'Too many requests. Please slow down and try again later.',
    500: 'Whoops, something went wrong on our servers.',
    503: 'Sorry, we are doing some maintenance. Please check back soon.',
  };
  return messages[props.status] || 'An error occurred while processing your request.';
});

const errorColorClass = computed(() => {
  if (props.status === 403 || props.status === 419) {
    return 'text-danger-600 dark:text-danger-400';
  } else if (props.status === 404) {
    return 'text-warning-600 dark:text-warning-400';
  } else {
    return 'text-danger-600 dark:text-danger-400';
  }
});

const goBack = () => {
  if (window.history.length > 1) {
    window.history.back();
  } else {
    router.visit('/');
  }
};
</script>
